import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import { Tab } from "@headlessui/react";

import { getMe, updateProfile } from "../../../api/profile";

import PersonalDetails from "../../elements/profile/PersonalDetails";
import ProjectCard from "../../cards/ProjectCard";

import { ReactComponent as User } from "../../../resources/icons/user.svg";
import { ReactComponent as Edit } from "../../../resources/icons/edit.svg";
import { ReactComponent as Logout } from "../../../resources/icons/logout.svg";
import { ReactComponent as Phone } from "../../../resources/icons/phone.svg";
import { ReactComponent as Email } from "../../../resources/icons/mail.svg";
import { ReactComponent as Bulstat } from "../../../resources/icons/bulstat.svg";
import { ReactComponent as Rating } from "../../../resources/icons/rating.svg";
import { ReactComponent as Star } from "../../../resources/icons/star.svg";
import { preventCachedImage } from "../../../utils/helperFunctions";
import { toast } from "react-toastify";
import { useAuthContext } from "../../../contexts/authContext";
import LogoutModal from "../../elements/profile/LogoutModal";
import { Project } from "../../../types";
import EditProfileModal from "../../elements/profile/EditProfileModal";
import Pagination from "../../elements/Pagination";

interface Review {
  id: number;
  rating: number;
  description?: string;
  submitter: {
    id: number;
    email: string;
    name: string;
    phone: string;
    type: string;
    image: string;
    memberSince: string;
  };
}

const ContractorProfile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<{
    id?: number;
    name: string;
    email: string;
    type: string;
    image: string;
    phone: string;
    bulstat: string;
    averageRating: number;
  }>({
    name: "",
    email: "",
    type: "",
    image: "",
    phone: "",
    bulstat: "",
    averageRating: 0,
  });

  const [reviews, setReviews] = useState<Review[]>([]);
  const [contractorProjects, setContractorProjects] = useState<Project[]>([]);
  const hasFetchedUser = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const { logout, loggingOut, setUser: setContextUser } = useAuthContext();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const paginatedProjects = contractorProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(contractorProjects.length / itemsPerPage);

  useEffect(() => {
    const fetchUser = async () => {
      if (hasFetchedUser.current) return;
      try {
        const userData = await getMe();
        setUser(userData);
        hasFetchedUser.current = true;
      } catch {
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (!user.id) return;

    const fetchContractorData = async () => {
      try {
        const reviewsRes = await axios.get(
          `${process.env.REACT_APP_URL}/contractors/${user.id}/reviews`,
          {
            params: { page: 1, limit: 20 },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "X-Client-Type": "web",
            },
          }
        );
        setReviews(reviewsRes.data.reviews || []);

        const projectsRes = await axios.get(
          `${process.env.REACT_APP_URL}/contractors/${user.id}/projects`,
          {
            params: { page: 1, limit: 30 },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "X-Client-Type": "web",
            },
          }
        );
        setContractorProjects(projectsRes.data.projects || []);
      } catch {
      }
    };

    fetchContractorData();
  }, [user.id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= rating;
      stars.push(
        <Star
          key={i}
          className={`w-5 h-5 ${isFilled ? "fill-[var(--warning-400)]" : ""}`}
          style={{ fill: isFilled ? "var(--warning-400)" : "#F2F4F7" }}
        />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };

  return (
    <div className="flex flex-row">
      <div className="flex-1 ml-[17.5rem]">
        <h1 className="p-6 text-2xl font-bold">My profile</h1>
        <div className="px-6 py-12 bg-[#F9FAFB] flex flex-row justify-between">
          <div className="flex flex-row">
            <div className="mr-4 self-center">
              {user.image ? (
                <img
                  className="w-[80px] h-[80px] object-cover rounded-full"
                  src={preventCachedImage(user.image)}
                  alt="Profile"
                />
              ) : (
                <User className="self-center" />
              )}
            </div>
            <div className="flex flex-col self-center">
              <h3 className="mb-1 font-bold text-xl">{user.name}</h3>
              <div>
                <span className="capitalize px-3 py-1 rounded-full text-xs font-medium border shrink-0 text-[#1672EC] bg-[#F0F9FF] border-[#B9E6FE]">
                  {user.type.charAt(0).toUpperCase() +
                    user.type.slice(1).toLowerCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Edit className="mr-2" />
              Edit profile
            </button>
            <button
              type="button"
              onClick={() => setIsLogoutModalOpen(true)}
              className="text-sm mb-2 flex flex-row px-4 py-2 bg-white border rounded-md font-semibold"
            >
              <Logout className="w-5 mr-2" style={{ color: "white" }} />
              Logout
            </button>
          </div>
        </div>

        <div className="p-6">
          <div>
            <h3 className="text-lg font-bold mb-4">Personal details</h3>
            <div className="flex">
              <PersonalDetails
                label="Phone number"
                Icon={Phone}
                info={user.phone}
              />
              <PersonalDetails label="Email" Icon={Email} info={user.email} />
              <PersonalDetails
                label="BULSTAT/Company number"
                Icon={Bulstat}
                info={user.bulstat}
              />
              <PersonalDetails
                label="Average rating"
                Icon={Rating}
                info={user.averageRating}
              />
            </div>
          </div>

          <div className="mt-8">
            <Tab.Group>
              <Tab.List className="inline-flex space-x-4 border-b border-gray-200">
                <Tab
                  className={({ selected }) =>
                    `px-4 py-2 text-sm font-medium ${
                      selected
                        ? "border-b-2 border-[#FC6A19] text-[#FC6A19]"
                        : "text-[#667085]"
                    } focus:outline-none focus:ring-0`
                  }
                >
                  Ratings
                </Tab>
                <Tab
                  className={({ selected }) =>
                    `px-4 py-2 text-sm font-medium ${
                      selected
                        ? "border-b-2 border-[#FC6A19] text-[#FC6A19]"
                        : "text-[#667085]"
                    } focus:outline-none focus:ring-0`
                  }
                >
                  Projects
                </Tab>
              </Tab.List>

              <Tab.Panels className="mt-4">
                <Tab.Panel>
                  {reviews.length === 0 ? (
                    <p className="text-gray-500">No reviews available.</p>
                  ) : (
                    <div className="space-y-6">
                      {reviews.map((review) => (
                        <div
                          key={review.id}
                          className="flex space-x-4 p-4 border-b rounded-lg"
                        >
                          <div className="w-12 h-12">
                            {review.submitter.image ? (
                              <img
                                src={preventCachedImage(review.submitter.image)}
                                alt={review.submitter.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              <User className="w-full h-full text-gray-300" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-col">
                              <h4 className="font-semibold text-base">
                                {review.submitter.name}
                              </h4>
                              {renderStars(review.rating)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Tab.Panel>

                <Tab.Panel>
                  {contractorProjects.length === 0 ? (
                    <p className="text-gray-500">No projects to display.</p>
                  ) : (
                    <>
                      <div className="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
                        {paginatedProjects.map((project) => (
                          <ProjectCard
                            key={project.id}
                            id={project.id}
                            title={project.title}
                            publishedAt={
                              project.publishedAt
                                ? dayjs(project.publishedAt).format(
                                    "DD.MM.YYYY"
                                  )
                                : dayjs(project.createdAt).format("DD.MM.YYYY")
                            }
                            status={
                              project.status.charAt(0).toUpperCase() +
                              project.status.slice(1).toLowerCase()
                            }
                            summary={project.summary || "No summary available."}
                          />
                        ))}
                      </div>
                      {totalPages > 1 && (
                        <div className="flex justify-center mt-6">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                          />
                        </div>
                      )}
                    </>
                  )}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      </div>

      <EditProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialValues={{
          phone: user.phone,
          image: user.image,
        }}
        onSubmit={async (values) => {
          try {
            const updatedUser = await updateProfile(values);
            setUser(updatedUser);
            setContextUser(updatedUser);
            setIsModalOpen(false);
            toast.success("Profile updated successfully");
          } catch {
            toast.error("Failed to update profile");
          }
        }}
        userRole="CONTRACTOR"
      />

      <LogoutModal
        isOpen={isLogoutModalOpen}
        closeModal={() => setIsLogoutModalOpen(false)}
        handleLogout={handleLogout}
        loading={loggingOut}
      />
    </div>
  );
};

export default ContractorProfile;
